import React from 'react'
import styled from 'styled-components'

/**
 * AbwaabPlus SegmentedTabBar — a #F5F5F5 track holding equal-width tabs; the
 * active tab is a white pill with shadow-sm. Used as a tab / segmented control.
 */
export type AbwTab = string | { value: string; label: React.ReactNode }

// `onChange` omitted: HTMLAttributes types it as a FormEventHandler, while
// this control reports the selected tab value.
export interface AbwSegmentedTabBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
	tabs?: AbwTab[]
	value?: string
	onChange?: (value: string) => void
}

export function SegmentedTabBar({ tabs = [], value, onChange, style, ...rest }: AbwSegmentedTabBarProps) {
	const norm = tabs.map(t => (typeof t === 'string' ? { value: t, label: t } : t))
	return (
		<Track style={style} {...rest}>
			{norm.map(t => {
				const on = t.value === value
				return (
					<button
						key={t.value}
						type="button"
						onClick={() => onChange?.(t.value)}
						style={{
							flex: '1 0 auto',
							minWidth: 'fit-content',
							whiteSpace: 'nowrap',
							height: 30,
							padding: '0 12px',
							border: 'none',
							cursor: 'pointer',
							borderRadius: 'var(--r-sm)',
							background: on ? 'var(--surface-card)' : 'transparent',
							boxShadow: on ? 'var(--sh-rest)' : 'none',
							font: 'var(--fw-medium) 14px var(--font-sans)',
							color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
							transition: 'background var(--dur-base) var(--ease), color var(--dur-base) var(--ease)',
						}}
					>
						{t.label}
					</button>
				)
			})}
		</Track>
	)
}

/* Tab sets wider than the track scroll horizontally (long curriculum tab
   names) instead of the labels wrapping out of the 30px pills; short sets
   keep the equal-width segmented look via flex-grow. */
const Track = styled.div`
	display: flex;
	gap: 0;
	padding: 3px;
	background: var(--surface-page);
	border-radius: var(--r-lg);
	overflow-x: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
`
